import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterTypes } from '../../../../../interfaces/src/lib/enums/index';
import {
  FilterOptionSliceType,
  PriceRangeFilterOptionType,
  AirportFilterResult,
} from '../../../../../interfaces/src/lib/types/index';
import { getDealsFilters } from '../../utilFuncs/contextAwareUtils';
import { getQueryParamIgnoreCase } from '../../utilFuncs/utilFuncs';
import { WritableDraft } from 'immer';

interface QueryParams {
  embkPort?: string | null;
  ships?: string | null;
  cabins?: string | null;
  airports?: string | null;
  destinations?: string | null;
  minPrice?: string | null;
  maxPrice?: string | null;
  priceRangeView?: string | null;
  cruiseId?: string | null;
  priceType?: string | null;
  priceCode?: string | null;
  portOfCall?: string | null;
  portCode?: string | null;
  categoryCd?: string | null;
}

function parseQueryInit(): {
  selectedPorts: FilterOptionSliceType[];
  selectedPortsExcursions: FilterOptionSliceType[];
  selectedCategories: FilterOptionSliceType[];
  selectedShips: FilterOptionSliceType[];
  selectedCabins: FilterOptionSliceType[];
  selectedAirports: FilterOptionSliceType[];
  selectedDestinations: FilterOptionSliceType[];
  selectedDestinationsExcursions: FilterOptionSliceType[];
  selectedPriceRange: FilterOptionSliceType[];
  selectedCruiseId: FilterOptionSliceType[];
  selectedPriceType: FilterOptionSliceType[];
  selectedPriceCode: FilterOptionSliceType[];
  selectedPortOfCall: FilterOptionSliceType[];
} {
  const queryParams: QueryParams = {
    embkPort: getQueryParamIgnoreCase(window.location.search, 'embkPort'),
    ships: getQueryParamIgnoreCase(window.location.search, 'ships'),
    cabins: getQueryParamIgnoreCase(window.location.search, 'cabins'),
    airports: getQueryParamIgnoreCase(window.location.search, 'airports'),
    destinations: getQueryParamIgnoreCase(window.location.search, 'area'),
    minPrice: getQueryParamIgnoreCase(window.location.search, 'minPrice'),
    maxPrice: getQueryParamIgnoreCase(window.location.search, 'maxPrice'),
    priceRangeView: getQueryParamIgnoreCase(window.location.search, 'priceRangeView'),
    cruiseId: getQueryParamIgnoreCase(window.location.search, 'cruiseId'),
    priceType: getQueryParamIgnoreCase(window.location.search, 'priceType'),
    priceCode: getQueryParamIgnoreCase(window.location.search, 'priceCode'),
    portOfCall: getQueryParamIgnoreCase(window.location.search, 'portOfCall'),
    portCode: getQueryParamIgnoreCase(window.location.search, 'embkPort'),
    categoryCd: getQueryParamIgnoreCase(window.location.search, 'categoryCd'),
  };

  const parseValues = (paramValue: string | null | undefined) => {
    if (!paramValue) return [];
    return paramValue
      .split(',')
      .map((item) => ({ name: '', value: item.trim(), useInSearch: true }));
  };

  const selectedPorts: FilterOptionSliceType[] = parseValues(queryParams.embkPort);
  const selectedShips: FilterOptionSliceType[] = parseValues(queryParams.ships);
  const selectedCabins: FilterOptionSliceType[] = parseValues(queryParams.cabins);
  const selectedAirports: FilterOptionSliceType[] = parseValues(queryParams.airports);
  const selectedDestinations: FilterOptionSliceType[] = parseValues(queryParams.destinations);
  const selectedCruiseId: FilterOptionSliceType[] = parseValues(queryParams.cruiseId);
  const selectedPriceType: FilterOptionSliceType[] = parseValues(queryParams.priceType);
  const selectedPriceCode: FilterOptionSliceType[] = parseValues(queryParams.priceCode);
  const selectedPortOfCall: FilterOptionSliceType[] = parseValues(queryParams.portOfCall);
  const selectedCategories: FilterOptionSliceType[] = parseValues(queryParams.categoryCd);
  const selectedDestinationsExcursions: FilterOptionSliceType[] = parseValues(
    queryParams.destinations
  );
  const selectedPortsExcursions: FilterOptionSliceType[] = parseValues(queryParams.portCode);

  let minPrice = queryParams.minPrice;
  let maxPrice = queryParams.maxPrice;
  const priceRangeView = queryParams.priceRangeView ? queryParams.priceRangeView : '';

  // Ensure minPrice and maxPrice are valid numbers
  if (minPrice && maxPrice && !isNaN(Number(minPrice)) && !isNaN(Number(maxPrice))) {
    if (Number(minPrice) > Number(maxPrice)) {
      [minPrice, maxPrice] = [maxPrice, minPrice];
    }
  }

  const selectedPriceRange: PriceRangeFilterOptionType[] = [];
  if (minPrice && !isNaN(Number(minPrice))) {
    selectedPriceRange.push({ name: 'minPrice', value: minPrice, viewType: priceRangeView });
  }

  if (maxPrice && !isNaN(Number(maxPrice))) {
    selectedPriceRange.push({ name: 'maxPrice', value: maxPrice, viewType: priceRangeView });
  }

  return {
    selectedPorts,
    selectedShips,
    selectedCabins,
    selectedAirports,
    selectedDestinations,
    selectedPriceRange,
    selectedCruiseId,
    selectedPriceCode,
    selectedPriceType,
    selectedPortOfCall,
    selectedCategories,
    selectedDestinationsExcursions,
    selectedPortsExcursions,
  };
}

export interface FacetFilterState {
  selectedDestinations: FilterOptionSliceType[];
  selectedDestinationsExcursions: FilterOptionSliceType[];
  selectedPorts: FilterOptionSliceType[];
  selectedPortsExcursions: FilterOptionSliceType[];
  selectedShips: FilterOptionSliceType[];
  selectedCabins: FilterOptionSliceType[];
  selectedAirports: FilterOptionSliceType[];
  isAirportToggleEnabled: boolean;
  priceRange: FilterOptionSliceType[];
  finalPriceRange?: FilterOptionSliceType[];
  selectedCruiseId: FilterOptionSliceType[];
  selectedPriceCode: FilterOptionSliceType[];
  selectedPriceType: FilterOptionSliceType[];
  selectedPortOfCall: FilterOptionSliceType[];
  selectedCategories: FilterOptionSliceType[];
  priceRangeChipLabel: string;
  priceRangePriceViewLabelPerPerson: string;
  priceRangePriceViewLabelPerCabin: string;
  priceRangeToasterAutoClosing: boolean;
  priceRangeToasterAutoClosingDelay: number;
  priceRangeToasterText: string;
  firstAirportResponse: {
    departureAirportCodes: Record<string, number>;
    departureAirportNames: Record<string, number>;
    shouldHideHasAir: boolean;
  } | null;
  airportsConfiguration: AirportFilterResult[] | null;
  pastBackup: {
    selectedPorts?: FilterOptionSliceType[];
    selectedPortsExcursions?: FilterOptionSliceType[];
    selectedShips?: FilterOptionSliceType[];
    selectedCabins?: FilterOptionSliceType[];
    selectedAirports?: FilterOptionSliceType[];
    selectedDestinations?: FilterOptionSliceType[];
    selectedDestinationsExcursions?: FilterOptionSliceType[];
    selectedFlights?: FilterOptionSliceType[];
    selectedPriceRange?: FilterOptionSliceType[];
    selectedCategories?: FilterOptionSliceType[];
  };
}

const initialState: FacetFilterState = {
  selectedDestinations: parseQueryInit().selectedDestinations,
  selectedDestinationsExcursions: parseQueryInit().selectedDestinationsExcursions,
  selectedPorts: parseQueryInit().selectedPorts,
  selectedPortsExcursions: parseQueryInit().selectedPortsExcursions,
  selectedShips: parseQueryInit().selectedShips,
  selectedCabins: parseQueryInit().selectedCabins,
  selectedAirports: parseQueryInit().selectedAirports,
  isAirportToggleEnabled: parseQueryInit().selectedAirports.length > 0,
  priceRange: parseQueryInit().selectedPriceRange,
  finalPriceRange: parseQueryInit().selectedPriceRange,
  selectedCruiseId: parseQueryInit().selectedCruiseId,
  selectedPriceCode: parseQueryInit().selectedPriceCode,
  selectedPriceType: parseQueryInit().selectedPriceType,
  selectedPortOfCall: parseQueryInit().selectedPortOfCall,
  selectedCategories: parseQueryInit().selectedCategories,
  priceRangeChipLabel: '',
  priceRangePriceViewLabelPerPerson: '',
  priceRangePriceViewLabelPerCabin: '',
  priceRangeToasterAutoClosing: false,
  priceRangeToasterAutoClosingDelay: 5000,
  firstAirportResponse: null,
  airportsConfiguration: null,
  pastBackup: {},
  priceRangeToasterText: '',
};

// Async thunk to reset the price range based on deal filters
export const resetDealsPriceRange = createAsyncThunk(
  'facetFilter/resetDealsPriceRange',
  async (_, { dispatch }) => {
    let dealsMinPrice: string | null = null;
    let dealsMaxPrice: string | null = null;

    // Fetch the deals filters
    const dealsFilters = getDealsFilters();

    // Extract min and max prices from deals filters
    dealsFilters.forEach((filter) => {
      if (filter.filterType === FilterTypes.MIN_PRICE) {
        dealsMinPrice = filter.filterValue;
      }
      if (filter.filterType === FilterTypes.MAX_PRICE) {
        dealsMaxPrice = filter.filterValue;
      }
    });

    // Prepare the data for updating state
    const selectedPriceRange: FilterOptionSliceType[] = [];
    if (dealsMinPrice) {
      selectedPriceRange.push({ name: 'minPrice', value: dealsMinPrice });
    }
    if (dealsMaxPrice) {
      selectedPriceRange.push({ name: 'maxPrice', value: dealsMaxPrice });
    }

    // Dispatch an action to reset the price range
    dispatch(setPriceRange(selectedPriceRange));
    dispatch(setFinalPriceRange(selectedPriceRange)); // Assuming you also want to reset finalPriceRange
  }
);

/** Reset airport filter to deal preconfigured state ("Any airport") when user clicks Clear on a deal page with airport preset. */
export const resetDealsAirportFilter = createAsyncThunk(
  'facetFilter/resetDealsAirportFilter',
  async (_, { dispatch, getState }) => {
    const dealsFilters = getDealsFilters();
    const airportDealFilter = dealsFilters.find((f) => f.filterType === FilterTypes.AIRPORT);
    if (!airportDealFilter?.filterValue) return;

    const state = getState() as { facetFilter: FacetFilterState };
    const airports = state.facetFilter.airportsConfiguration;
    const hasAirName =
      airports?.find((a) => a.airportCode === 'hasAir')?.airportName || 'Any airport';

    dispatch(setSelectedAirports([{ name: hasAirName, value: 'hasAir' }]));
  }
);

const facetFilterSlice = createSlice({
  initialState,
  name: 'facetFilter',
  reducers: {
    resetDestinationFilters: (state) => {
      state.selectedDestinations = [];
    },
    resetDestinationExcursionsFilters: (state) => {
      state.selectedDestinationsExcursions = [];
    },
    resetPortFilters: (state) => {
      state.selectedPorts = [];
    },
    resetPortExcursionsFilters: (state) => {
      state.selectedPortsExcursions = [];
    },
    resetCategoryFilters: (state) => {
      state.selectedCategories = [];
    },
    resetShipFilters: (state) => {
      state.selectedShips = [];
    },
    resetCabinFilters: (state) => {
      state.selectedCabins = [];
    },
    resetAirportFilters: (state) => {
      state.selectedAirports = [];
    },
    resetPriceRangeFilters: (state) => {
      state.priceRange = [];
    },
    resetFinalPriceRangeFilters: (state) => {
      state.finalPriceRange = [];
    },
    resetCruiseIdFilters: (state) => {
      state.selectedCruiseId = [];
    },
    resetPriceCodeFilters: (state) => {
      state.selectedPriceCode = [];
    },
    resetPriceTypeFilters: (state) => {
      state.selectedPriceType = [];
    },
    resetPortOfCallFilters: (state) => {
      state.selectedPortOfCall = [];
    },
    resetFacetBackup: (state) => {
      state.pastBackup = {};
    },
    restoreFacetState: (state) => {
      if (Object.keys(state.pastBackup).length === 0) return;
      if (state.pastBackup.selectedDestinations)
        state.selectedDestinations = state.pastBackup.selectedDestinations;
      if (state.pastBackup.selectedPorts) state.selectedPorts = state.pastBackup.selectedPorts;
      if (state.pastBackup.selectedDestinationsExcursions)
        state.selectedDestinationsExcursions = state.pastBackup.selectedDestinationsExcursions;
      if (state.pastBackup.selectedPortsExcursions)
        state.selectedPortsExcursions = state.pastBackup.selectedPortsExcursions;
      if (state.pastBackup.selectedCategories)
        state.selectedCategories = state.pastBackup.selectedCategories;
      if (state.pastBackup.selectedShips) state.selectedShips = state.pastBackup.selectedShips;
      if (state.pastBackup.selectedCabins) state.selectedCabins = state.pastBackup.selectedCabins;
      if (state.pastBackup.selectedAirports)
        state.selectedAirports = state.pastBackup.selectedAirports;
      if (state.pastBackup.selectedPriceRange)
        state.priceRange = state.pastBackup.selectedPriceRange;
      state.pastBackup = {};
    },
    setFacetBackup: (
      state,
      action: PayloadAction<{
        filterType: string;
        filterValue: WritableDraft<FilterOptionSliceType>[] | undefined;
      }>
    ) => {
      switch (action.payload.filterType) {
        case FilterTypes.DESTINATION:
          if (!state?.pastBackup?.selectedDestinations)
            state.pastBackup.selectedDestinations = action.payload.filterValue;
          break;
        case FilterTypes.PORT:
          if (!state?.pastBackup?.selectedPorts)
            state.pastBackup.selectedPorts = action.payload.filterValue;
          break;
        case FilterTypes.CATEGORY:
          if (!state?.pastBackup?.selectedCategories)
            state.pastBackup.selectedCategories = action.payload.filterValue;
          break;
        case FilterTypes.SHIP:
          if (!state?.pastBackup?.selectedShips)
            state.pastBackup.selectedShips = action.payload.filterValue;
          break;
        case FilterTypes.CABIN:
          if (!state?.pastBackup?.selectedCabins)
            state.pastBackup.selectedCabins = action.payload.filterValue;
          break;
        case FilterTypes.AIRPORT:
          if (!state?.pastBackup?.selectedAirports)
            state.pastBackup.selectedAirports = action.payload.filterValue;
          break;
        case FilterTypes.PRICE_RANGE:
          if (!state?.pastBackup?.selectedPriceRange)
            state.pastBackup.selectedPriceRange = action.payload.filterValue;
          break;
      }
    },
    setFinalPriceRange: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.finalPriceRange = action.payload;
    },
    setPortOfCall: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedPortOfCall = action.payload;
    },
    setPriceCode: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedPriceCode = action.payload;
    },
    setPriceRange: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.priceRange = action.payload;
    },
    setPriceRangeChipLabel: (state, action: PayloadAction<string>) => {
      state.priceRangeChipLabel = action.payload;
    },
    setPriceRangePriceViewLabelPerPerson: (state, action: PayloadAction<string>) => {
      state.priceRangePriceViewLabelPerPerson = action.payload;
    },
    setPriceRangePriceViewLabelPerCabin: (state, action: PayloadAction<string>) => {
      state.priceRangePriceViewLabelPerCabin = action.payload;
    },
    setPriceRangeToasterAutoClosing: (state, action: PayloadAction<boolean>) => {
      state.priceRangeToasterAutoClosing = action.payload;
    },
    setPriceRangeToasterAutoClosingDelay: (state, action: PayloadAction<number>) => {
      state.priceRangeToasterAutoClosingDelay = action.payload;
    },
    setPriceRangeToasterText: (state, action: PayloadAction<string>) => {
      state.priceRangeToasterText = action.payload;
    },
    setPriceType: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedPriceType = action.payload;
    },
    setSelectedCabins: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedCabins = action.payload;
    },
    setSelectedAirports: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedAirports = action.payload;
    },
    setIsAirportToggleEnabled: (state, action: PayloadAction<boolean>) => {
      state.isAirportToggleEnabled = action.payload;
    },
    setSelectedCategories: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedCategories = action.payload;
    },
    setSelectedCruiseId: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedCruiseId = action.payload;
    },
    setSelectedDestinations: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedDestinations = action.payload;
    },
    setSelectedPorts: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedPorts = action.payload;
    },
    setSelectedDestinationsExcursions: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedDestinationsExcursions = action.payload;
    },
    setSelectedPortsExcursions: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedPortsExcursions = action.payload;
    },
    setSelectedShips: (state, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.selectedShips = action.payload;
    },
    setFirstAirportResponse: (
      state,
      action: PayloadAction<{
        departureAirportCodes: Record<string, number>;
        departureAirportNames: Record<string, number>;
        shouldHideHasAir: boolean;
      } | null>
    ) => {
      state.firstAirportResponse = action.payload;
    },
    setAirportsConfiguration: (state, action: PayloadAction<AirportFilterResult[] | null>) => {
      state.airportsConfiguration = action.payload;
    },
  },
});

export const {
  setSelectedDestinations,
  setSelectedDestinationsExcursions,
  setSelectedPorts,
  setSelectedPortsExcursions,
  setSelectedShips,
  setSelectedCabins,
  setSelectedAirports,
  setIsAirportToggleEnabled,
  setSelectedCategories,
  setPriceRange,
  setFinalPriceRange,
  setPriceRangeChipLabel,
  setPriceRangePriceViewLabelPerPerson,
  setPriceRangePriceViewLabelPerCabin,
  setPriceRangeToasterAutoClosing,
  setPriceRangeToasterAutoClosingDelay,
  setPriceRangeToasterText,
  setSelectedCruiseId,
  setPriceType,
  setPriceCode,
  setPortOfCall,
  resetDestinationFilters,
  resetDestinationExcursionsFilters,
  resetShipFilters,
  resetCabinFilters,
  resetAirportFilters,
  resetPriceRangeFilters,
  resetFinalPriceRangeFilters,
  resetCruiseIdFilters,
  resetPriceCodeFilters,
  resetPriceTypeFilters,
  resetPortOfCallFilters,
  resetPortFilters,
  resetPortExcursionsFilters,
  resetCategoryFilters,
  setFacetBackup,
  restoreFacetState,
  resetFacetBackup,
  setFirstAirportResponse,
  setAirportsConfiguration,
} = facetFilterSlice.actions;
export default facetFilterSlice.reducer;
